import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, Brain, Palette, AppWindow, ShoppingBag, Calendar, Briefcase, Video, Image, Wrench, Home, Globe2, RotateCcw, MessageSquare, Package2, Share2, Sparkles } from 'lucide-react';
import { SidebarItem } from './SidebarItem';
import { useSidebar } from '../../hooks/useSidebar';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useChatbotConfig } from '../../contexts/ChatbotConfigContext';
import { AlertDialog } from '../ui/AlertDialog';
import { useMiniApps } from '../../hooks/useMiniApps';
import { useDesignColors } from '../../hooks/useDesignColors';

// Organiser les items par catégories
const categorizedMenuItems = [
  {
    title: 'General',
    items: [
      { icon: Brain, label: 'AI Settings', path: 'ai', id: 'ai_settings', badge: null },
      { icon: Globe2, label: 'Multilingual Settings', path: 'multilingual', id: 'multilingual_settings', badge: null },
      { icon: Palette, label: 'Design Settings', path: 'design', id: 'design', badge: null },
      { icon: Wrench, label: 'Widget Settings', path: 'widget', id: 'widget_settings', badge: null },
      { icon: MessageSquare, label: 'Chatbot Limits', path: 'limits', id: 'limits', badge: null }
    ]
  },
  {
    title: 'Mini Apps',
    items: [
      { icon: AppWindow, label: 'Mini Apps Overview', path: 'mini-apps', id: null, badge: null },
      { icon: ShoppingBag, label: 'Products', path: 'products', id: 'products', badge: null },
      { icon: Package2, label: 'Order Tracking', path: 'order-tracking', id: 'order-tracking', badge: { text: 'New', color: 'indigo' } },
      { icon: Calendar, label: 'Calendly', path: 'calendar', id: 'calendar', badge: null },
      { icon: Briefcase, label: 'Services', path: 'services', id: 'services', badge: null },
      { icon: Video, label: 'Videos', path: 'videos', id: 'videos', badge: null },
      { icon: Image, label: 'Gallery', path: 'gallery', id: 'gallery', badge: null },
      { icon: Home, label: 'Real Estate', path: 'realestate', id: 'realestate', badge: null },
      { icon: Share2, label: 'Social Media', path: 'social', id: 'social', badge: { text: 'Beta', color: 'purple' } }
    ]
  }
];

export function Sidebar() {
  const { isOpen, toggle, setIsOpen } = useSidebar();
  const { chatbotPublicId } = useChatbot();
  const { updateConfigSection } = useChatbotConfig();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const { miniApps } = useMiniApps();
  const location = useLocation();
  const [previousPath, setPreviousPath] = useState<string | null>(null);
  const { colors } = useDesignColors();
  
  // Hook simple pour détecter si l'écran est en mode mobile
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    // Fonction pour vérifier la taille de l'écran
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    // Vérifier initialement
    checkMobile();
    
    // Ajouter un écouteur d'événement pour les changements de taille
    window.addEventListener('resize', checkMobile);
    
    // Nettoyer l'écouteur d'événement
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Suivre les changements de chemin pour les animations
  useEffect(() => {
    setPreviousPath(location.pathname);
  }, [location.pathname]);

  // Déterminer si nous venons de naviguer vers cette page
  const isNewNavigation = (path: string) => {
    return location.pathname.includes(path) && !previousPath?.includes(path);
  };

  const handleResetChatbot = async () => {
    if (!chatbotPublicId) return;

    try {
      // Reset all config sections to their default values
      await Promise.all([
        updateConfigSection('colors', null),
        updateConfigSection('texts', null),
        updateConfigSection('quick_actions', null),
        updateConfigSection('mini_apps', null),
        updateConfigSection('button_colors', null),
        updateConfigSection('logos', null),
        updateConfigSection('ai_settings', null),
        updateConfigSection('calendly_config', null),
        updateConfigSection('theme_settings', null),
        updateConfigSection('widget_settings', null),
        updateConfigSection('multilingual_settings', null)
      ]);

      // Close the dialog
      setShowResetDialog(false);

      // Reload the page to reflect changes
      window.location.reload();
    } catch (err) {
      console.error('Error resetting chatbot:', err);
    }
  };

  useEffect(() => {
    if (!initialized && chatbotPublicId) {
      // Initialize mini apps (we'll use another approach since initialize doesn't exist)
      setInitialized(true);
    }
  }, [chatbotPublicId, initialized]);

  // Filtrer les éléments de menu basés sur les mini-apps activées
  const filteredCategories = React.useMemo(() => {
    if (!initialized) {
      return categorizedMenuItems.map(category => ({
        ...category,
        items: category.items.filter(item => item.id === null || category.title !== 'Mini Apps')
      }));
    }

    return categorizedMenuItems.map(category => {
      if (category.title !== 'Mini Apps') {
        return category;
      }

      // Pour la catégorie Mini Apps, filtrer selon les apps activées
      const filteredItems = category.items.filter(item => {
        // Toujours inclure l'élément d'aperçu des Mini Apps
        if (item.id === null) return true;
        
        // Pour les autres pages de mini-apps, inclure uniquement si la mini-app est activée
        return miniApps.some(app => app.id === item.id && app.enabled);
      });

      return {
        ...category,
        items: filteredItems
      };
    });
  }, [miniApps, initialized]);

  // Fonction pour gérer le clic sur un lien et fermer la sidebar en mode mobile
  const handleLinkClick = () => {
    if (isMobile) {
      setIsOpen(false);
    }
  };

  return (
    <div className="h-full relative overflow-hidden">
      {/* Arrière-plan amélioré avec dégradé subtil et effet de flou */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-xl z-0"></div>
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black opacity-80 z-0"></div>
      
      {/* Effet de lueur subtil sur les bords */}
      <div className="absolute right-0 top-0 bottom-0 w-px bg-gradient-to-b from-blue-500/10 via-violet-500/10 to-transparent"></div>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/10 to-transparent"></div>
      
      {/* Particules/dégradés d'ambiance */}
      <div className="absolute -top-20 -left-20 w-60 h-60 rounded-full bg-gradient-to-br from-blue-500/5 to-indigo-500/5 blur-3xl"></div>
      <div className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full bg-gradient-to-br from-violet-500/5 to-purple-500/5 blur-3xl"></div>

      <div className={`relative h-full transition-all duration-500 ease-in-out flex flex-col z-10 ${
        isOpen ? 'w-64' : 'w-20'
      }`}>
        <div className="flex items-center justify-between p-5 border-b border-white/10 bg-gradient-to-r from-white/5 to-transparent">
          <Link 
            to="/app/dashboard"
            className={`text-blue-400 hover:text-blue-300 transition-colors ${
              !isOpen ? 'mx-auto' : ''
            }`}
          >
            <Home className="w-5 h-5" />
          </Link>
          
          {isOpen && (
            <span className="text-white font-medium ml-2">Settings</span>
          )}
          
          <button
            onClick={toggle}
            className={`bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full w-8 h-8 flex items-center justify-center shadow-lg shadow-indigo-950/40 hover:shadow-indigo-500/30 hover:scale-105 transition-all duration-300 ${
              !isOpen ? 'absolute left-1/2 -translate-x-1/2 mt-2' : ''
            }`}
          >
            <ChevronLeft className={`w-5 h-5 text-white transition-transform duration-300 ${
              !isOpen ? 'rotate-180' : ''
            }`} />
          </button>
        </div>

        {/* Menu scrollable */}
        <div className="flex-1 overflow-y-auto py-5 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent hover:scrollbar-thumb-white/20 px-3">
          {filteredCategories.map((category, categoryIndex) => (
            <div key={category.title} className="mb-6">
              {/* Titre de la catégorie */}
              {isOpen && (
                <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider px-3 mb-3">
                  {category.title}
                </h3>
              )}
              
              {/* Éléments de la catégorie */}
              <div className="space-y-1">
                {category.items.map((item) => {
                  const fullPath = item.path.startsWith('/') ? item.path : `/app/settings/${item.path}`;
                  const isActive = location.pathname === fullPath || 
                                   (item.path !== 'mini-apps' && location.pathname.includes(item.path));
                  const isHovered = hoveredItem === item.path;
                  const isNew = isNewNavigation(item.path);
                  
                  return (
                    <Link
                      key={item.path}
                      to={fullPath}
                      onClick={handleLinkClick} // Ferme la sidebar en mode mobile
                      onMouseEnter={() => setHoveredItem(item.path)}
                      onMouseLeave={() => setHoveredItem(null)}
                      className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-300 overflow-hidden ${
                        isOpen ? 'mx-1' : 'justify-center'
                      } ${
                        isActive 
                          ? 'text-white' 
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      {/* Effet de fond amélioré lors du hover ou état actif */}
                      {(isActive || isHovered) && (
                        <span 
                          className={`absolute inset-0 rounded-xl transition-all duration-300 ${
                            isActive 
                              ? `bg-gradient-to-r ${colors.gradientStart ? `from-${colors.gradientStart}/20 to-${colors.gradientEnd}/20` : 'from-blue-600/20 to-indigo-600/20'} border border-blue-500/30`
                              : 'bg-white/5'
                          }`}
                          style={{
                            animationName: isNew ? 'sidebar-indicator-appear' : 'none',
                            animationDuration: '0.5s',
                            animationTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
                            animationFillMode: 'backwards'
                          }}
                        ></span>
                      )}

                      {/* Indicateur de menu actif amélioré */}
                      {isActive && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-gradient-to-b from-blue-400 to-indigo-500 rounded-r-full shadow-[0_0_8px_rgba(59,130,246,0.5)]"></span>
                      )}

                      {/* Icône avec animation améliorée */}
                      <item.icon className={`w-5 h-5 shrink-0 transition-all duration-300 ${
                        isActive 
                          ? `text-${colors.gradientStart || 'blue-400'}`
                          : 'text-gray-400 group-hover:text-white'
                      } ${
                        isHovered && !isActive ? 'scale-110 rotate-3' : ''
                      }`} />

                      {/* Texte avec animation */}
                      {isOpen && (
                        <div className="flex-1 flex items-center justify-between min-w-0">
                          <span className={`truncate transition-all duration-300 ${
                            isActive ? 'font-medium' : ''
                          }`}>
                            {item.label}
                          </span>
                          
                          {/* Badge pour les nouvelles fonctionnalités ou en bêta */}
                          {item.badge && (
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ml-1 flex items-center whitespace-nowrap ${
                              item.badge.color === 'indigo' 
                                ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                                : 'bg-violet-500/20 text-violet-300 border border-violet-500/30'
                            }`}>
                              {item.badge.color === 'indigo' && (
                                <Sparkles className="w-3 h-3 mr-0.5 text-blue-300" />
                              )}
                              {item.badge.text}
                            </span>
                          )}
                        </div>
                      )}
                    </Link>
                  );
                })}
              </div>
              
              {/* Séparateur entre les catégories */}
              {categoryIndex < filteredCategories.length - 1 && isOpen && (
                <div className="my-4 mx-3 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
              )}
            </div>
          ))}
        </div>

        {/* Bouton de réinitialisation */}
        <div className="p-3 border-t border-white/10 bg-black/20 relative backdrop-blur-sm">
          <button
            onClick={() => setShowResetDialog(true)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors ${
              !isOpen && 'justify-center'
            }`}
          >
            <RotateCcw className="w-5 h-5 shrink-0" />
            {isOpen && <span className="text-sm">Reset All Settings</span>}
          </button>
        </div>

        <AlertDialog
          isOpen={showResetDialog}
          onClose={() => setShowResetDialog(false)}
          title="Reset Chatbot Settings"
          description="Are you sure you want to reset your chatbot? This action is irreversible and will delete all your custom settings."
          confirmLabel="Reset"
          confirmVariant="danger"
          onConfirm={handleResetChatbot}
        />
      </div>
    </div>
  );
}